import React, { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Package, ShoppingBag, Lightbulb, Mail as MailIcon, Image as ImageIcon, Plus, Edit, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { id: "products", label: "Προϊόντα", icon: Package },
  { id: "orders", label: "Παραγγελίες", icon: ShoppingBag },
  { id: "projects", label: "Η ιδέα σου", icon: Lightbulb },
  { id: "artworks", label: "Artworks", icon: ImageIcon },
  { id: "messages", label: "Μηνύματα", icon: MailIcon },
];

const ORDER_STATUSES = [
  { value: "pending", label: "Εκκρεμεί" },
  { value: "confirmed_cod", label: "Επιβεβαιωμένη (COD)" },
  { value: "paid", label: "Πληρωμένη" },
  { value: "shipped", label: "Αποστάλθηκε" },
  { value: "delivered", label: "Παραδόθηκε" },
  { value: "cancelled", label: "Ακυρωμένη" },
];

const PROJECT_STATUSES = [
  { value: "new", label: "Νέο" },
  { value: "in_review", label: "Σε αξιολόγηση" },
  { value: "quoted", label: "Δόθηκε προσφορά" },
  { value: "done", label: "Ολοκληρώθηκε" },
  { value: "cancelled", label: "Ακυρώθηκε" },
];

export const AdminDashboard = () => {
  const { username, logout, authHeaders } = useAuth();
  const [tab, setTab] = useState("products");

  return (
    <main className="min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-10">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">
              Admin Panel
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter">
              Καλώς ήρθες, <span className="text-[#B026FF] neon-glow-pink">{username}</span>
            </h1>
          </div>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 border border-white/10 text-white px-4 py-2.5 rounded-lg hover:border-[#B026FF] hover:text-[#B026FF] transition-all"
            data-testid="admin-logout"
          >
            <LogOut size={16} /> Αποσύνδεση
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/5 pb-4">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm transition-all ${
                tab === t.id
                  ? "bg-[#B026FF] text-black font-bold"
                  : "border border-white/10 text-zinc-300 hover:border-white/30"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "products" && <ProductsAdmin authHeaders={authHeaders} />}
        {tab === "orders" && <OrdersAdmin authHeaders={authHeaders} />}
        {tab === "projects" && <ProjectsAdmin authHeaders={authHeaders} />}
        {tab === "artworks" && <ArtworksAdmin authHeaders={authHeaders} />}
        {tab === "messages" && <MessagesAdmin authHeaders={authHeaders} />}
      </div>
    </main>
  );
};

// ============== PRODUCTS ==============
const emptyProduct = {
  slug: "",
  name: "",
  description: "",
  price: 0,
  image: "",
  category: "Αγάπη & Ρομαντισμός",
  color: "Ροζ",
  size: "",
  in_stock: true,
};

const ProductsAdmin = ({ authHeaders }) => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await axios.get(`${API}/products`);
    setProducts(data);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (form, id) => {
    const payload = {
      ...form,
      price: parseFloat(form.price),
      in_stock: form.in_stock !== false,
    };
    try {
      if (id) {
        await axios.put(`${API}/admin/products/${id}`, payload, { headers: authHeaders() });
        toast.success("Το προϊόν ενημερώθηκε επιτυχώς");
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: authHeaders() });
        toast.success("Το προϊόν δημιουργήθηκε επιτυχώς");
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (err) {
      const status = err?.response?.status;
      let detail = err?.response?.data?.detail;
      // Pydantic validation returns array of errors
      if (Array.isArray(detail)) {
        detail = detail.map((e) => `${e.loc?.slice(-1)[0] || ""}: ${e.msg}`).join(", ");
      }
      if (status === 401 || status === 403) {
        toast.error("Η σύνδεσή σου έληξε. Κάνε ξανά login.");
      } else {
        toast.error(`Σφάλμα αποθήκευσης: ${detail || err.message || "Unknown"}`);
      }
      // Keep form open so user can retry
      console.error("Admin save error:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Σίγουρα διαγραφή;")) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers: authHeaders() });
      toast.success("Διαγράφηκε");
      load();
    } catch (err) {
      toast.error("Σφάλμα διαγραφής");
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex justify-between items-center mb-6">
        <div className="text-zinc-400 text-sm">{products.length} προϊόντα</div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 bg-[#00FFFF] text-black px-4 py-2 rounded-lg font-bold hover:neon-box-glow-cyan transition-all text-sm"
          data-testid="admin-product-add"
        >
          <Plus size={16} /> Νέο προϊόν
        </button>
      </div>

      {(creating || editing) && (
        <ProductForm
          initial={editing || emptyProduct}
          authHeaders={authHeaders}
          onCancel={() => { setEditing(null); setCreating(false); }}
          onSave={(form) => handleSave(form, editing?.id)}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} className="bg-[#050505] border border-white/5 rounded-xl p-4 flex gap-4" data-testid={`admin-product-${p.id}`}>
            <img src={p.image} alt={p.name} className="w-24 h-24 rounded-lg object-cover bg-black flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{p.name}</div>
              <div className="text-xs text-zinc-500 mt-1">{p.category}</div>
              <div className="text-[#00FFFF] font-bold mt-2">{p.price.toFixed(2)}€</div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => setEditing(p)}
                  className="text-xs px-2 py-1 rounded border border-white/10 hover:border-[#B026FF] hover:text-[#B026FF] inline-flex items-center gap-1"
                  data-testid={`admin-product-edit-${p.id}`}
                >
                  <Edit size={12} /> Edit
                </button>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="text-xs px-2 py-1 rounded border border-white/10 hover:border-red-500 hover:text-red-500 inline-flex items-center gap-1"
                  data-testid={`admin-product-delete-${p.id}`}
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductForm = ({ initial, onCancel, onSave, authHeaders }) => {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = React.useRef(null);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Auto-generate slug from name if slug is empty
  const slugify = (s) =>
    (s || "")
      .toLowerCase()
      .trim()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // remove Greek/Latin accents
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60);

  const handleNameChange = (e) => {
    const v = e.target.value;
    setForm((f) => ({
      ...f,
      name: v,
      // Auto-fill slug ONLY if user hasn't manually typed one
      slug: f.slug && f.slug !== slugify(f.name) ? f.slug : slugify(v),
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Μόνο εικόνες επιτρέπονται.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Η εικόνα υπερβαίνει τα 8MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload-image`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, image: fullUrl }));
      toast.success("Η εικόνα ανέβηκε!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Αποτυχία upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    // Ensure slug exists (auto-generate if empty)
    const finalForm = {
      ...form,
      slug: form.slug?.trim() || slugify(form.name),
      size: form.size?.trim() || "40x60cm",
      color: form.color?.trim() || "Neon",
    };
    if (!finalForm.image) {
      toast.error("Ανέβασε εικόνα ή δώσε URL για την εικόνα");
      return;
    }
    setSaving(true);
    try {
      await onSave(finalForm);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submitForm}
      className="bg-[#050505] border border-[#B026FF]/40 rounded-xl p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid="admin-product-form"
    >
      <Field label="Όνομα" required><input className="ainput" value={form.name} onChange={handleNameChange} required data-testid="admin-field-name" /></Field>
      <Field label="Slug (URL)"><input className="ainput" value={form.slug} onChange={upd("slug")} placeholder="auto από όνομα" data-testid="admin-field-slug" /></Field>
      <Field label="Τιμή (€)" required><input type="number" step="0.01" min="0" className="ainput" value={form.price} onChange={upd("price")} required data-testid="admin-field-price" /></Field>
      <Field label="Κατηγορία" required><input className="ainput" value={form.category} onChange={upd("category")} required data-testid="admin-field-category" /></Field>
      <Field label="Χρώμα"><input className="ainput" value={form.color} onChange={upd("color")} placeholder="π.χ. Ροζ" data-testid="admin-field-color" /></Field>
      <Field label="Μέγεθος"><input className="ainput" value={form.size} onChange={upd("size")} placeholder="40x60cm" data-testid="admin-field-size" /></Field>

      <div className="md:col-span-2 flex flex-col gap-2">
        <span className="text-xs text-zinc-400 font-medium">
          Εικόνα προϊόντος <span className="text-[#B026FF]">*</span>
        </span>
        <div className="flex flex-col sm:flex-row gap-3 items-start">
          {form.image && (
            <img
              src={form.image}
              alt="Preview"
              className="w-32 h-32 object-cover rounded-lg border border-white/10 bg-black flex-shrink-0"
              data-testid="admin-product-image-preview"
            />
          )}
          <div className="flex-1 flex flex-col gap-2 w-full">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="admin-product-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center justify-center gap-2 bg-[#00FFFF] text-black px-4 py-2.5 rounded-lg font-bold text-sm hover:neon-box-glow-cyan transition-all disabled:opacity-50"
              data-testid="admin-product-upload-btn"
            >
              {uploading ? "Ανέβασμα..." : "📷 Ανέβασε εικόνα (max 8MB)"}
            </button>
            <div className="text-xs text-zinc-500">ή επικόλλησε URL:</div>
            <input
              className="ainput"
              value={form.image}
              onChange={upd("image")}
              placeholder="https://..."
              required
              data-testid="admin-product-image-url"
            />
          </div>
        </div>
      </div>

      <Field label="Περιγραφή" required span>
        <textarea rows={3} className="ainput resize-none" value={form.description} onChange={upd("description")} required />
      </Field>
      <div className="md:col-span-2 flex flex-col-reverse sm:flex-row gap-3 mt-4 sticky bottom-0 bg-[#050505] pt-4 -mx-6 -mb-6 px-6 pb-6 border-t border-[#B026FF]/20">
        <button
          type="button"
          onClick={onCancel}
          className="border border-white/15 text-zinc-300 px-5 py-3 rounded-lg hover:bg-white/5 transition-colors"
          data-testid="admin-product-cancel"
        >
          Άκυρο
        </button>
        <button
          type="submit"
          disabled={saving || uploading}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#B026FF] text-white px-6 py-3 rounded-lg font-black text-base uppercase tracking-wider hover:neon-box-glow-pink transition-all disabled:opacity-50 shadow-[0_0_20px_rgba(176,38,255,0.4)]"
          data-testid="admin-product-save"
        >
          {saving ? "💾 Αποθήκευση..." : "💾 Αποθήκευση Προϊόντος"}
        </button>
      </div>

      <style>{`.ainput{width:100%;background:#0A0A0A;border:1px solid rgba(255,255,255,0.08);color:white;padding:.6rem .9rem;border-radius:.5rem;font-size:.9rem;outline:none}.ainput:focus{border-color:#B026FF}`}</style>
    </form>
  );
};

const Field = ({ label, required, span, children }) => (
  <label className={`flex flex-col gap-2 ${span ? "md:col-span-2" : ""}`}>
    <span className="text-xs text-zinc-400 font-medium">{label} {required && <span className="text-[#B026FF]">*</span>}</span>
    {children}
  </label>
);

// ============== ORDERS ==============
const OrdersAdmin = ({ authHeaders }) => {
  const [orders, setOrders] = useState([]);
  const load = async () => {
    const { data } = await axios.get(`${API}/admin/orders`, { headers: authHeaders() });
    setOrders(data);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${id}/status`, { status }, { headers: authHeaders() });
      toast.success("Status ενημερώθηκε");
      load();
    } catch {
      toast.error("Σφάλμα");
    }
  };

  return (
    <div data-testid="admin-orders" className="flex flex-col gap-3">
      <div className="text-zinc-400 text-sm mb-2">{orders.length} παραγγελίες</div>
      {orders.length === 0 && <div className="text-zinc-500 py-12 text-center">Δεν υπάρχουν παραγγελίες ακόμα.</div>}
      {orders.map((o) => (
        <div key={o.id} className="bg-[#050505] border border-white/5 rounded-xl p-5" data-testid={`admin-order-${o.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-mono text-xs text-zinc-500">#{o.id.slice(0, 8)}</div>
              <div className="font-semibold mt-1">{o.customer.full_name} · {o.customer.email}</div>
              <div className="text-xs text-zinc-500 mt-1">{o.customer.phone} · {o.customer.address}, {o.customer.city} {o.customer.postal_code}</div>
              <div className="text-[10px] text-zinc-600 mt-2">{new Date(o.created_at).toLocaleString("el-GR")}</div>
            </div>
            <div className="text-right">
              <div className="text-[#00FFFF] font-bold text-xl">{o.total.toFixed(2)}€</div>
              <div className="text-xs text-zinc-400 mt-1">{o.payment_method === "cod" ? "Αντικαταβολή" : "Κάρτα"}</div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <div className="text-sm text-zinc-300">
              <span className="text-zinc-500">Προϊόντα: </span>
              {o.items.map((i, idx) => (
                <span key={idx}>{i.name} ×{i.quantity}{idx < o.items.length - 1 ? ", " : ""}</span>
              ))}
            </div>
            <select
              value={o.status}
              onChange={(e) => updateStatus(o.id, e.target.value)}
              className="bg-[#0A0A0A] border border-white/10 text-white text-sm px-3 py-2 rounded-lg"
              data-testid={`admin-order-status-${o.id}`}
            >
              {ORDER_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============== PROJECTS ==============
const ProjectsAdmin = ({ authHeaders }) => {
  const [projects, setProjects] = useState([]);
  const load = async () => {
    const { data } = await axios.get(`${API}/admin/projects`, { headers: authHeaders() });
    setProjects(data);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/projects/${id}/status`, { status }, { headers: authHeaders() });
      toast.success("Status ενημερώθηκε");
      load();
    } catch {
      toast.error("Σφάλμα");
    }
  };

  return (
    <div data-testid="admin-projects" className="flex flex-col gap-3">
      <div className="text-zinc-400 text-sm mb-2">{projects.length} αιτήματα</div>
      {projects.length === 0 && <div className="text-zinc-500 py-12 text-center">Δεν υπάρχουν custom projects ακόμα.</div>}
      {projects.map((p) => (
        <div key={p.id} className="bg-[#050505] border border-white/5 rounded-xl p-5" data-testid={`admin-project-${p.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{p.name}</div>
              <div className="text-xs text-zinc-500 mt-1">
                <a href={`mailto:${p.email}`} className="hover:text-[#B026FF] inline-flex items-center gap-1">
                  {p.email} <ExternalLink size={10} />
                </a>
              </div>
              {p.phrase && <div className="text-[#B026FF] font-bold text-sm mt-2">"{p.phrase}"</div>}
              <p className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap leading-relaxed">{p.description}</p>
              {p.files?.length > 0 && (
                <div className="text-xs text-zinc-500 mt-2">📎 {p.files.length} συνημμένα αρχεία (ελέγξτε το email σας)</div>
              )}
              <div className="text-[10px] text-zinc-600 mt-3">{new Date(p.created_at).toLocaleString("el-GR")}</div>
            </div>
            <select
              value={p.status}
              onChange={(e) => updateStatus(p.id, e.target.value)}
              className="bg-[#0A0A0A] border border-white/10 text-white text-sm px-3 py-2 rounded-lg"
              data-testid={`admin-project-status-${p.id}`}
            >
              {PROJECT_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============== ARTWORKS ==============
const ARTWORK_STATUSES = [
  { value: "new", label: "Νέο" },
  { value: "in_review", label: "Σε αξιολόγηση" },
  { value: "quoted", label: "Δόθηκε προσφορά" },
  { value: "done", label: "Ολοκληρώθηκε" },
  { value: "cancelled", label: "Ακυρώθηκε" },
];

const ArtworksAdmin = ({ authHeaders }) => {
  const [items, setItems] = useState([]);
  const load = async () => {
    const { data } = await axios.get(`${API}/admin/artworks`, { headers: authHeaders() });
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/artworks/${id}/status`, { status }, { headers: authHeaders() });
      toast.success("Status ενημερώθηκε");
      load();
    } catch {
      toast.error("Σφάλμα");
    }
  };

  return (
    <div data-testid="admin-artworks" className="flex flex-col gap-3">
      <div className="text-zinc-400 text-sm mb-2">{items.length} αιτήματα</div>
      {items.length === 0 && <div className="text-zinc-500 py-12 text-center">Δεν υπάρχουν artwork αιτήματα ακόμα.</div>}
      {items.map((a) => (
        <div key={a.id} className="bg-[#050505] border border-white/5 rounded-xl p-5" data-testid={`admin-artwork-${a.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{a.name}</div>
              <div className="text-xs text-zinc-500 mt-1">
                <a href={`mailto:${a.email}`} className="hover:text-[#B026FF] inline-flex items-center gap-1">
                  {a.email} <ExternalLink size={10} />
                </a>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 text-xs">
                <span className="px-2 py-1 rounded-full bg-[#B026FF]/10 text-[#B026FF] border border-[#B026FF]/30">
                  {a.placement === "suggest" ? "Ζητά προτάσεις" : "Δικές του θέσεις"}
                </span>
                {a.preferred_color && (
                  <span className="px-2 py-1 rounded-full bg-white/5 text-zinc-300 border border-white/10">
                    Χρώμα: {a.preferred_color}
                  </span>
                )}
                <span className="px-2 py-1 rounded-full bg-white/5 text-zinc-300 border border-white/10">
                  📎 {a.files?.length || 0} φωτό
                </span>
              </div>
              {a.placement_notes && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1">Θέσεις neon</div>
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{a.placement_notes}</p>
                </div>
              )}
              {a.description && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1">Ιστορία / Λεπτομέρειες</div>
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{a.description}</p>
                </div>
              )}
              <div className="text-xs text-zinc-500 mt-3">Δείτε τα συνημμένα στο email σας</div>
              <div className="text-[10px] text-zinc-600 mt-2">{new Date(a.created_at).toLocaleString("el-GR")}</div>
            </div>
            <select
              value={a.status}
              onChange={(e) => updateStatus(a.id, e.target.value)}
              className="bg-[#0A0A0A] border border-white/10 text-white text-sm px-3 py-2 rounded-lg"
              data-testid={`admin-artwork-status-${a.id}`}
            >
              {ARTWORK_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============== MESSAGES ==============
const MessagesAdmin = ({ authHeaders }) => {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/contact-messages`, { headers: authHeaders() }).then((r) => setMsgs(r.data));
  }, [authHeaders]);

  return (
    <div data-testid="admin-messages" className="flex flex-col gap-3">
      <div className="text-zinc-400 text-sm mb-2">{msgs.length} μηνύματα</div>
      {msgs.length === 0 && <div className="text-zinc-500 py-12 text-center">Δεν υπάρχουν μηνύματα ακόμα.</div>}
      {msgs.map((m) => (
        <div key={m.id} className="bg-[#050505] border border-white/5 rounded-xl p-5" data-testid={`admin-message-${m.id}`}>
          <div className="font-semibold">{m.name}</div>
          <a href={`mailto:${m.email}`} className="text-xs text-zinc-500 hover:text-[#B026FF]">{m.email}</a>
          <p className="text-sm text-zinc-300 mt-3 whitespace-pre-wrap leading-relaxed">{m.message}</p>
          <div className="text-[10px] text-zinc-600 mt-3">{new Date(m.created_at).toLocaleString("el-GR")}</div>
        </div>
      ))}
    </div>
  );
};

export default AdminDashboard;
